import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const TOKEN_BALANCE_DISPLAY_DECIMALS: Record<string, number> = {
  COTI: 4,
  "p.COTI": 4,
  WETH: 6,
  "p.WETH": 6,
  USDC: 4,
  "p.USDC": 4,
  USDT: 4,
  "p.USDT": 4,
  WBTC: 6,
  "p.WBTC": 6,
  WADA: 6,
  "p.WADA": 6,
};

export function expandExponentialNumber(numStr: string): string {
  const eString = numStr.toLowerCase().trim();
  if (!eString.includes("e")) return numStr;

  const negative = eString.startsWith("-");
  const unsigned = negative || eString.startsWith("+") ? eString.slice(1) : eString;
  const [base, exp] = unsigned.split("e");
  if (base == null || exp == null || base === "") return numStr;

  const expNum = parseInt(exp, 10);
  if (!Number.isFinite(expNum)) return numStr;

  let [intPart, decPart = ""] = base.split(".");
  if (intPart.startsWith("+")) intPart = intPart.slice(1);

  let expanded: string;
  if (expNum === 0) {
    expanded = decPart ? `${intPart}.${decPart}` : intPart;
  } else if (expNum > 0) {
    decPart = decPart.padEnd(expNum, "0");
    expanded = `${intPart}${decPart.slice(0, expNum)}.${decPart.slice(expNum)}`;
  } else {
    const absExp = Math.abs(expNum);
    intPart = intPart.padStart(absExp + 1, "0");
    expanded = `${intPart.slice(0, -absExp)}.${intPart.slice(-absExp)}${decPart}`;
  }

  if (expanded.endsWith(".")) expanded = expanded.slice(0, -1);
  return negative ? `-${expanded}` : expanded;
}

/** Expand JS scientific notation (e.g. 1e-18 → 0.000…001). */
export function formatPlainDecimal(value: string | number): string {
  return expandExponentialNumber(String(value));
}

/**
 * Dust floors from on-chain 1-wei style limits. Below this, treat as unset for UI.
 * (~a few wei on 18-decimal tokens; still far below any meaningful USDC atomic unit).
 */
export const DUST_AMOUNT_THRESHOLD = 1e-15;

export function isDustAmount(value: string | number | null | undefined): boolean {
  if (value == null || value === "") return false;
  const n = typeof value === "number" ? value : parseFloat(formatPlainDecimal(value));
  return Number.isFinite(n) && n > 0 && n < DUST_AMOUNT_THRESHOLD;
}

/**
 * Human label for portal/bridge min/max amount limits.
 * - N/A / Error preserved
 * - 0 kept (admin "no cap" convention for max)
 * - dust mins shown as "—" instead of 1e-18 / 0.000…001
 * - absurdly large / non-finite max sentinels → "0" (uncapped)
 * - otherwise plain decimal (never scientific notation)
 */
export function formatAmountLimitDisplay(value?: string | null): string {
  if (value == null || value === "") return "N/A";
  if (value === "N/A" || value === "Error") return value;
  const plain = formatPlainDecimal(value);
  const n = parseFloat(plain);
  // Non-finite or impossibly large (uint128.max-style) ⇒ uncapped.
  if (!Number.isFinite(n) || n >= 1e15) return "0";
  if (n === 0) return "0";
  if (n > 0 && n < DUST_AMOUNT_THRESHOLD) return "—";
  if (!plain.includes(".")) return plain;
  return plain.replace(/(\.\d*?[1-9])0+$/u, "$1").replace(/\.0+$/u, "");
}

export function truncateDecimalValue(value: string | number, decimals: number): string {
  let numStr = String(value);
  if (numStr === '' || numStr === 'NaN') return '0';

  numStr = expandExponentialNumber(numStr);

  const [integerPart, decimalPart] = numStr.split('.');
  if (!decimalPart || decimals === 0) {
    return integerPart;
  }

  let truncatedDecimal = decimalPart.slice(0, decimals);

  // Remove trailing zeros
  truncatedDecimal = truncatedDecimal.replace(/0+$/, '');

  return truncatedDecimal ? `${integerPart}.${truncatedDecimal}` : integerPart;
}

export function formatTokenBalanceDisplay(symbol: string, balance: string | number): string {
  const decimals = TOKEN_BALANCE_DISPLAY_DECIMALS[symbol] ?? 4;
  return truncateDecimalValue(balance, decimals);
}

export function addThousandsSeparators(value: string | number): string {
  let [integerPart, decimalPart] = String(value).split('.');
  integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decimalPart !== undefined ? `${integerPart}.${decimalPart}` : integerPart;
}

export function formatBalanceWithNotation(value: string | number): string {
  const plain = formatPlainDecimal(value);
  const numValue = parseFloat(plain);
  
  // If the number is 0 or very small, return expanded plain decimal (never "1e-18")
  if (numValue === 0 || Math.abs(numValue) < 1) {
    return addThousandsSeparators(plain);
  }
  
  // Check if the number has decimal places (cents)
  const hasDecimals = plain.includes('.') && numValue % 1 !== 0;
  
  // If it has decimals, use regular formatting
  if (hasDecimals) {
    const formatted = addThousandsSeparators(plain);
    return applyDigitMask(formatted);
  }
  
  // For round numbers, use M, B, T notation only
  const absValue = Math.abs(numValue);
  const sign = numValue < 0 ? '-' : '';
  
  if (absValue >= 1_000_000_000_000) {
    // Trillions - show up to 2 decimal places, but don't truncate unnecessarily
    const exactValue = absValue / 1_000_000_000_000;
    const truncated = Math.floor(exactValue * 100) / 100;
    const formatted = truncated.toString().replace(/\.0+$/, ''); // Only remove trailing zeros after decimal point
    return `${sign}${formatted}T...`;
  } else if (absValue >= 1_000_000_000) {
    // Billions - show up to 2 decimal places, but don't truncate unnecessarily
    const exactValue = absValue / 1_000_000_000;
    const truncated = Math.floor(exactValue * 100) / 100;
    const formatted = truncated.toString().replace(/\.0+$/, ''); // Only remove trailing zeros after decimal point
    return `${sign}${formatted}B...`;
  } else if (absValue >= 1_000_000) {
    // Millions - show up to 2 decimal places, but don't truncate unnecessarily
    const exactValue = absValue / 1_000_000;
    const truncated = Math.floor(exactValue * 100) / 100;
    const formatted = truncated.toString().replace(/\.0+$/, ''); // Only remove trailing zeros after decimal point
    return `${sign}${formatted}M...`;
  }
  
  // For numbers less than 1 million, return as is with separators and digit mask
  const formatted = addThousandsSeparators(plain);
  return applyDigitMask(formatted);
}

export function applyDigitMask(formattedValue: string): string {
  // Count only digits (excluding commas, dots, and signs)
  const digitCount = formattedValue.replace(/[^0-9]/g, '').length;
  
  // If 10 digits or fewer, return as is
  if (digitCount <= 10) {
    return formattedValue;
  }
  
  // For large whole numbers that will be formatted with B/T notation, don't mask them
  // This allows formatBalanceWithNotation to work properly
  const numValue = parseFloat(formattedValue.replace(/,/g, ''));
  if (!isNaN(numValue) && numValue >= 1_000_000 && !formattedValue.includes('.')) {
    return formattedValue; // Let formatBalanceWithNotation handle it
  }
  
  // Extract sign if present
  const sign = formattedValue.startsWith('-') ? '-' : '';
  const valueWithoutSign = formattedValue.replace(/^-/, '');
  
  // Find the first 10 digits and their positions
  let digitsSeen = 0;
  let truncateIndex = 0;
  
  for (let i = 0; i < valueWithoutSign.length; i++) {
    if (/[0-9]/.test(valueWithoutSign[i])) {
      digitsSeen++;
      if (digitsSeen === 10) {
        truncateIndex = i + 1;
        break;
      }
    }
  }
  
  // Truncate and add ellipsis
  const truncated = valueWithoutSign.substring(0, truncateIndex);
  return `${sign}${truncated}...`;
}

/**
 * Formats a balance into a compact, space-friendly string for display.
 * - Values >= 1,000 are abbreviated with K / M / B / T suffixes (e.g. "1.18M", "1.305B").
 * - Smaller values keep up to `decimals` decimal places with thousands separators.
 *
 * Examples:
 *   formatCompactBalance("1048567.938") => "1.05M"
 *   formatCompactBalance("100000045383") => "100B"
 *   formatCompactBalance("44058.4766")  => "44.06K"
 *   formatCompactBalance("45454")       => "45.45K"
 *   formatCompactBalance("1.5")         => "1.5"
 */
export function formatCompactBalance(value: string | number, decimals = 2): string {
  const plain = formatPlainDecimal(value);
  const num = parseFloat(plain);
  if (isNaN(num)) return "0";

  const abs = Math.abs(num);
  const sign = num < 0 ? "-" : "";

  const units: { threshold: number; suffix: string }[] = [
    { threshold: 1_000_000_000_000, suffix: "T" },
    { threshold: 1_000_000_000, suffix: "B" },
    { threshold: 1_000_000, suffix: "M" },
    { threshold: 1_000, suffix: "K" },
  ];

  for (const { threshold, suffix } of units) {
    if (abs >= threshold) {
      const scaled = abs / threshold;
      // Trim trailing zeros (e.g. "100.00" -> "100", "1.50" -> "1.5")
      const formatted = formatPlainDecimal(parseFloat(scaled.toFixed(decimals)));
      return `${sign}${formatted}${suffix}`;
    }
  }

  // Below 1,000 — show with separators, trimming trailing zeros on decimals
  const formatted = formatPlainDecimal(parseFloat(abs.toFixed(decimals)));
  return `${sign}${addThousandsSeparators(formatted)}`;
}
